import React, { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useFormik } from "formik";
import moment from "moment";
import { toast } from "react-toastify";
import Pagination from "react-js-pagination";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import { addLettersCertificates } from "../schemas";

const DISCIPLINARY_TYPES = ["Warning Letter"];

const printPathForType = (documentType, id) => {
  if (documentType === "Warning Letter") return `/warning-letter/${id}`;
  return null;
};

export default function StaffProfileDisciplinary({ staffBranchId }) {
  const { id } = useParams();
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [loading, setLoading] = useState(false);
  const [myData, setMyData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const resolvedBranch = staffBranchId || branch_id || "";

  const date = new Date();
  const defaultValue = date.toLocaleDateString("en-CA");
  const eDate = moment(defaultValue).format("YYYY-MM-DD");

  const fetchRecords = async (page = 1, pageLimit = limit) => {
    setLoading(true);
    setCurrentPage(page);
    setLimit(pageLimit);
    try {
      // Prefer staff branch; empty branch still returns this user's letters for reviewers.
      const response = await UserServices.getCertificatesLetters(
        resolvedBranch,
        pageLimit,
        page,
        id,
        { document_type: "Warning Letter" }
      );

      if (response.status === 200) {
        const rows = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setMyData(
          rows.filter(
            (row) =>
              DISCIPLINARY_TYPES.includes(row.document_type) ||
              /warning|misconduct|disciplin/i.test(
                `${row.document_type || ""} ${row.subject || ""}`
              )
          )
        );
        setLimit(response.data.pagination?.per_page ?? pageLimit);
        setTotalResults(response.data.pagination?.total_record ?? rows.length);
        setTotalPages(response.data.pagination?.total_pages ?? 1);
        setCurrentPage(response.data.pagination?.current_page ?? page);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setMyData([]);
        setTotalResults(0);
        setTotalPages(1);
      } else {
        console.log(err);
        toast.error(err.response?.data?.message || "Failed to load disciplinary records");
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (id) fetchRecords(1, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, resolvedBranch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      branch_id: resolvedBranch,
      user_id: id,
      document_category: "Letter",
      document_type: "Warning Letter",
      issue_date: eDate,
      document_code: "",
      subject: "",
    },
    validationSchema: addLettersCertificates,
    onSubmit: async (values, action) => {
      setLoading(true);
      try {
        const code =
          (values.document_code || "").trim() ||
          `WL-${id}-${moment().format("YYYYMMDD-HHmm")}`;
        const response = await UserServices.addCertificatesLetters({
          ...values,
          branch_id: String(values.branch_id || resolvedBranch || ""),
          user_id: String(id),
          document_category: "Letter",
          document_type: "Warning Letter",
          document_code: code,
          subject: values.subject || "Warning Letter",
        });
        if (response.status === 201 || response.status === 200) {
          toast.success("Warning letter added");
          action.resetForm();
          fetchRecords(1, limit);
        }
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            (err.response?.status === 422
              ? "Validation failed"
              : "Failed to add warning letter")
        );
      }
      setLoading(false);
    },
  });

  return (
    <div className="row g-3 mt-2">
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white py-3">
            <h5 className="mb-0">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Disciplinary Action
            </h5>
            <small className="text-muted">
              Warning letters and misconduct records for this staff member
            </small>
          </div>
          <div className="card-body">
            <form className="row g-3 mb-4" onSubmit={formik.handleSubmit}>
              <div className="col-md-3">
                <label className="form-label">Issue Date</label>
                <input
                  type="date"
                  name="issue_date"
                  className="form-control"
                  value={formik.values.issue_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.issue_date && formik.errors.issue_date ? (
                  <p className="text-danger mb-0">{formik.errors.issue_date}</p>
                ) : null}
              </div>
              <div className="col-md-3">
                <label className="form-label">Document Code</label>
                <input
                  type="text"
                  name="document_code"
                  className="form-control"
                  placeholder="Optional"
                  value={formik.values.document_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Subject / Reason</label>
                <input
                  type="text"
                  name="subject"
                  className="form-control"
                  placeholder="e.g. Late attendance / Misconduct"
                  value={formik.values.subject}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.subject && formik.errors.subject ? (
                  <p className="text-danger mb-0">{formik.errors.subject}</p>
                ) : null}
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                  + Add Warning
                </button>
              </div>
            </form>

            <div className="table-responsive">
              <table className="table table-striped table-borderless table-style-1">
                <thead>
                  <tr>
                    <th>Sr#</th>
                    <th>Type</th>
                    <th>Subject</th>
                    <th>Issue Date</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : myData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted">
                        No disciplinary records found for this staff member.
                      </td>
                    </tr>
                  ) : (
                    myData.map((row, index) => {
                      const printPath = printPathForType(row.document_type, row.id);
                      return (
                        <tr key={row.id}>
                          <td>{(currentPage - 1) * limit + index + 1}</td>
                          <td>{row.document_type}</td>
                          <td>{row.subject || "—"}</td>
                          <td>
                            {row.issue_date
                              ? moment(row.issue_date).format("DD-MM-YYYY")
                              : "—"}
                          </td>
                          <td>{row.document_code || "—"}</td>
                          <td>
                            {String(row.status) === "0" ? "Inactive" : "Active"}
                          </td>
                          <td>
                            {printPath ? (
                              <Link className="print-btn" to={printPath}>
                                <i className="bi bi-printer"></i>{" "}
                                <span className="inative-text">Print</span>
                              </Link>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {totalResults > limit ? (
              <div className="d-flex justify-content-center mt-3">
                <Pagination
                  activePage={currentPage}
                  itemsCountPerPage={limit}
                  totalItemsCount={Number(totalResults)}
                  pageRangeDisplayed={5}
                  onChange={(page) => fetchRecords(page, limit)}
                  itemClass="page-item"
                  linkClass="page-link"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
